import axios, { AxiosResponse } from "axios";
import { AppDispatch } from "../store";
import { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import {
  setOrder,
  setOrderList,
  setIsWaitingOrderPostResponse,
  setIsWaitingOrderPutResponse,
  setOrderUser,
  setOrderListByUserId,
  setIsWaitingOrderGetResponse,
  setQueryOrderList,
  setImportedArticlesCount,
  setIsWaitingOrderExportResponse,
  setIsWaitingRevertOrderExportResponse,
  setIsWaitingOrderHistoryGetResponse,
  setOrderHistoryList,
  setPdfVerification,
  setEmailHistory,
  setIsWaitingSendOrderEmailResponse,
  setIsWaitingStockResponse,
  setArticleStockValues,
  setHasUpdatedStock,
  setIsWaitingPriceProposalPreviewResponse,
  setIsWaitingOrderStatusSyncResponse,
} from "../slices/orderSlice";
import { getApiUrl } from "../helpers/environmentHelper";
import { Order } from "../../models/responses/order";
import { OrderUser } from "../../models/responses/orderUser";
import { QueryOrder } from "../../models/responses/queryOrder";
import { QueryOrderRequest } from "../../models/requests/queryOrderRequest";
import { Article } from "../../models/responses/article";
import { OrderExportRequest } from "../../models/requests/orderExportRequest";
import { ClientType } from "../../models/enums/clientTypeEnum";
import { handleFloatToInt } from "./calculationsHelperService";
import { OrderHistory } from "../../models/responses/orderHistory";
import { acquireApiToken } from "../helpers/apiTokenHelper";
import { EmailHistory } from "../../models/responses/emailHistory";

export const getOrders =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    statusId: string | undefined,
  ) =>
    async (dispatch: AppDispatch) => {
      const baseUrl = getApiUrl(window.location.hostname, 1);
      dispatch(setIsWaitingOrderGetResponse(true));

      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            if (response) {
              const getData: Order[] = (
                await axios.get(`${baseUrl}/orders/status/${statusId}`, {
                  timeout: 10000,
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${response.accessToken}`,
                  },
                })
              ).data;
              dispatch(setIsWaitingOrderGetResponse(false));
              dispatch(setOrderList({ orders: getData }));
            }
          });
        }
      } catch (error) {
        dispatch(setIsWaitingOrderGetResponse(false));
        console.log(error);
      }
    };

export const queryOrders =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    statusId: string | undefined,
    page: number,
    pageSize: number,
    queryText: string,
    orderBy: string,
    sortDesc: boolean,
    silent: boolean = false,
    clientType: ClientType,
  ) =>
    async (dispatch: AppDispatch) => {
      const baseUrl = getApiUrl(window.location.hostname, 2);
      if (!silent) dispatch(setIsWaitingOrderGetResponse(true));
      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            if (response) {
              const query: QueryOrderRequest = {
                page,
                pageSize,
                statusId,
                queryText,
                orderBy,
                sortDesc,
                clientType,
              };
              const getData: QueryOrder = (
                await axios.post(`${baseUrl}/orders/query`, query, {
                  timeout: 10000,
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${response.accessToken}`,
                  },
                })
              ).data;
              dispatch(setQueryOrderList({ query: getData }));
              dispatch(setIsWaitingOrderGetResponse(false));
            }
          });
        }
      } catch (error) {
        dispatch(setIsWaitingOrderGetResponse(false));
        console.log(error);
      }
    };

export const getOrderById =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    id: string | undefined,
  ) =>
    async (dispatch: AppDispatch) => {
      if (!id) return;

      dispatch(setIsWaitingOrderGetResponse(true));
      const baseUrl = getApiUrl(window.location.hostname, 1);

      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            if (response) {
              const getData: Order = (
                await axios.get(`${baseUrl}/orders/${id}`, {
                  timeout: 20000,
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${response.accessToken}`,
                  },
                })
              ).data;
              dispatch(setIsWaitingOrderGetResponse(false));
              dispatch(setOrder({ order: getData }));
            }
          });
        }
      } catch (error) {
        dispatch(setIsWaitingOrderGetResponse(false));
        console.log(error);
      }
    };

export const postOrder =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    order: Order,
    onSuccess: (orderId: string | null) => void,
    onError?: (errorType: string, errorMessage?: string) => void,
  ) =>
    async (dispatch: AppDispatch) => {
      dispatch(setIsWaitingOrderPostResponse(true));
      order = handleOrderForRequest(order);
      const baseUrl = getApiUrl(window.location.hostname, 2);
      let responseId: string | null = null;
      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            try {
              if (response) {
                try {
                  const responseData = await axios.post(
                    `${baseUrl}/orders`,
                    order,
                    {
                      timeout: 10000,
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${response.accessToken}`,
                      },
                    },
                  );
                  if (responseData.status === 201) {
                    let locationArray =
                      responseData.headers["location"].split("/");
                    responseId = locationArray[locationArray.length - 1];
                  }
                } catch (err: any) {
                  if (err.response && err.response.status === 409) {
                    const msg =
                      "Det finns redan en order med detta PostNord-id i systemet.";
                    if (onError) onError("ALREADY_EXISTS", msg);
                    return;
                  } else {
                    if (onError) onError("UNKNOWN", err.message);
                    return;
                  }
                }
              }
            } finally {
              onSuccess(responseId);
              dispatch(setIsWaitingOrderPostResponse(false));
            }
          });
        }
      } catch (error: any) {
        if (onError) onError("UNKNOWN", error.message);
        else console.log(error);
      }
    };

export const putOrder =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    order: Order,
    onSuccess: (orderId: string | null) => void,
    onError?: (errorType: string, errorMessage?: string) => void,
  ) =>
    async (dispatch: AppDispatch) => {
      const baseUrl = getApiUrl(window.location.hostname, 2);
      dispatch(setIsWaitingOrderPutResponse(true));
      let dto = { ...order };
      dto = handleOrderForRequest(dto);

      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            try {
              if (response) {
                try {
                  const responseData = await axios.put(`${baseUrl}/orders`, dto, {
                    timeout: 10000,
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${response.accessToken}`,
                    },
                  });
                  if (responseData.status === 204) {
                    onSuccess(dto.id);
                  }
                } catch (err: any) {
                  if (err.response && err.response.status === 409) {
                    const msg =
                      "Det finns redan en order med detta PostNord-id i systemet.";
                    if (onError) onError("ALREADY_EXISTS", msg);
                    return;
                  } else {
                    if (onError) onError("UNKNOWN", err.message);
                    return;
                  }
                }
              }
            } finally {
              dispatch(setIsWaitingOrderPutResponse(false));
            }
          });
        }
      } catch (error: any) {
        if (onError) onError("UNKNOWN", error.message);
        else console.log(error);
      }
    };

export const assignOrder =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    id: string | undefined,
  ) =>
    async () => {
      if (!id) return;
      const baseUrl = getApiUrl(window.location.hostname, 1);
      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            if (response) {
              await axios.patch(
                `${baseUrl}/orders/${id}/assign`,
                {},
                {
                  timeout: 10000,
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${response.accessToken}`,
                  },
                },
              );
            }
          });
        }
      } catch (error) {
        console.log(error);
      }
    };

export const removeAssignOrder =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    id: string | undefined,
  ) =>
    async () => {
      if (!id) return;
      const baseUrl = getApiUrl(window.location.hostname, 1);
      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            if (response) {
              await axios.patch(
                `${baseUrl}/orders/${id}/remove-assign`,
                {},
                {
                  timeout: 10000,
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${response.accessToken}`,
                  },
                },
              );
            }
          });
        }
      } catch (error) {
        console.log(error);
      }
    };

export const getOrderUserById =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    id: string | undefined,
  ) =>
    async (dispatch: AppDispatch) => {
      if (!id) return;
      dispatch(setIsWaitingOrderGetResponse(true));
      const baseUrl = getApiUrl(window.location.hostname, 1);
      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            try {
              if (response) {
                const getData: OrderUser = (
                  await axios.get(`${baseUrl}/orders/${id}/user`, {
                    timeout: 10000,
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${response.accessToken}`,
                    },
                  })
                ).data;
                dispatch(setIsWaitingOrderGetResponse(false));
                dispatch(setOrderUser({ orderUser: getData }));
              }
            } catch {
              dispatch(setIsWaitingOrderGetResponse(false));
            }
          });
        }
      } catch (error) {
        dispatch(setIsWaitingOrderGetResponse(false));
        console.log(error);
      }
    };

export const getUserOrders =
  (instance: IPublicClientApplication, account: AccountInfo | null) =>
    async (dispatch: AppDispatch) => {
      const baseUrl = getApiUrl(window.location.hostname, 1);
      dispatch(setIsWaitingOrderGetResponse(true));
      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            if (response) {
              const getData: Order[] = (
                await axios.get(`${baseUrl}/orders/get-assigned-orders`, {
                  timeout: 10000,
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${response.accessToken}`,
                  },
                })
              ).data;
              dispatch(setOrderListByUserId({ orders: getData }));
              dispatch(setIsWaitingOrderGetResponse(false));
            }
          });
        }
      } catch (error) {
        dispatch(setIsWaitingOrderGetResponse(false));
        console.log(error);
      }
    };

export const exportOrder =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    id: string | undefined,
    onSuccess: (orderId: string | null, isUnprocessableEntity?: boolean) => void,
  ) =>
    async (dispatch: AppDispatch) => {
      if (!id) return;
      const baseUrl = getApiUrl(window.location.hostname, 1);
      dispatch(setIsWaitingOrderExportResponse(true));
      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            if (response) {
              try {
                const result = await axios.patch(
                  `${baseUrl}/orders/${id}/export`,
                  {},
                  {
                    timeout: 10000,
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${response.accessToken}`,
                    },
                  },
                );
                if (result.status === 204) {
                  dispatch(setIsWaitingOrderExportResponse(false));
                  onSuccess(id, false);
                }
              } catch (err: any) {
                if (err.response && err.response.status === 422) {
                  dispatch(setIsWaitingOrderExportResponse(false));
                  onSuccess(id, true);
                  return;
                } else {
                  dispatch(setIsWaitingOrderExportResponse(false));
                  onSuccess(null, false);
                  return;
                }
              }
            }
          });
        }
      } catch (error) {
        dispatch(setIsWaitingOrderExportResponse(false));
        console.log(error);
      }
    };

export const revertExportOrder =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    id: string | undefined,
    onSuccess: (orderId: string | null) => void,
  ) =>
    async (dispatch: AppDispatch) => {
      if (!id) return;
      const baseUrl = getApiUrl(window.location.hostname, 1);
      dispatch(setIsWaitingRevertOrderExportResponse(true));
      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            if (response) {
              const result = await axios.patch(
                `${baseUrl}/orders/${id}/revert-export`,
                {},
                {
                  timeout: 10000,
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${response.accessToken}`,
                  },
                },
              );
              if (result.status === 204) {
                dispatch(setIsWaitingRevertOrderExportResponse(false));
                onSuccess(id);
              }
            }
          });
        }
      } catch (error) {
        dispatch(setIsWaitingRevertOrderExportResponse(false));
        console.log(error);
      }
    };

export const syncOrders =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    onSuccess: (success: boolean | null) => void,
  ) =>
    async (dispatch: AppDispatch) => {
      const baseUrl = getApiUrl(window.location.hostname, 1);
      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            if (response) {
              const result: AxiosResponse<Article[]> = await axios.patch(
                `${baseUrl}/orders/synchronize-all`,
                {},
                {
                  timeout: 10000,
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${response.accessToken}`,
                  },
                },
              );

              if (result.status === 204 || result.status === 200) {
                dispatch(setImportedArticlesCount(result.data?.length ?? 0));
                onSuccess(true);
              } else {
                onSuccess(false);
              }
            }
          });
        }
      } catch (error) {
        console.log(error);
      }
    };

export const queryOrdersExport =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    fromDate: Date,
    toDate: Date,
    clientType: ClientType,
    silent: boolean = false,
  ) =>
    async (dispatch: AppDispatch) => {
      const baseUrl = getApiUrl(window.location.hostname, 2);
      if (!silent) dispatch(setIsWaitingOrderExportResponse(true));
      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            if (response) {
              const query: OrderExportRequest = {
                fromDate,
                toDate,
                clientType,
              };
              const result = await axios.post(
                `${baseUrl}/orders/export-orders`,
                query,
                {
                  timeout: 10000,
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${response.accessToken}`,
                  },
                  responseType: "blob",
                },
              );

              const bom = "\uFEFF";
              const blob = new Blob([bom, result.data], {
                type: "text/csv;charset=utf-8;",
              });
              const url = window.URL.createObjectURL(blob);
              const now = new Date();
              const dateStr = now.toISOString().split("T")[0];
              const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, "-");
              const fileName = `exported_orders_${dateStr}_${timeStr}.csv`;

              const link = document.createElement("a");
              link.href = url;
              link.setAttribute("download", fileName);
              document.body.appendChild(link);
              link.click();

              if (link.parentNode) {
                link.parentNode.removeChild(link);
              }
              window.URL.revokeObjectURL(url);

              dispatch(setIsWaitingOrderExportResponse(false));
            }
          });
        }
      } catch (error) {
        dispatch(setIsWaitingOrderExportResponse(false));
        console.log(error);
      }
    };

export const getOrderHistory =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    orderId: string | undefined,
  ) =>
    async (dispatch: AppDispatch) => {
      console.log("Fetching order history...");
      const baseUrl = getApiUrl(window.location.hostname, 2);
      dispatch(setIsWaitingOrderHistoryGetResponse(true));
      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            if (response) {
              const getData: OrderHistory[] = (
                await axios.get(`${baseUrl}/orders/${orderId}/order-history`, {
                  timeout: 10000,
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${response.accessToken}`,
                  },
                })
              ).data;
              console.log("Order history fetched:", getData);
              dispatch(setIsWaitingOrderHistoryGetResponse(false));
              dispatch(setOrderHistoryList({ history: getData }));
            }
          });
        }
      } catch (error) {
        dispatch(setIsWaitingOrderHistoryGetResponse(false));
        console.log(error);
      }
    };

export const getPdfVerification =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    id: string | undefined,
  ) =>
    async (dispatch: AppDispatch) => {
      if (!id) return;

      const baseUrl = getApiUrl(window.location.hostname, 2);
      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            if (response) {
              const getData: Blob = (
                await axios.get(`${baseUrl}/orders/${id}/pdf-verification`, {
                  timeout: 20000,
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${response.accessToken}`,
                  },
                  responseType: "blob",
                })
              ).data;

              dispatch(setPdfVerification(getData));
              return;
            }
          });
        }
      } catch (error) {
        console.log(error);
      }
    };

export const getPriceProposalPreview =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    id: string | undefined,
  ) =>
    async (dispatch: AppDispatch) => {
      if (!id) return;

      const baseUrl = getApiUrl(window.location.hostname, 2);
      dispatch(setIsWaitingPriceProposalPreviewResponse(true));
      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            if (response) {
              const getData: Blob = (
                await axios.get(`${baseUrl}/orders/${id}/price-proposal-preview`, {
                  timeout: 20000,
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${response.accessToken}`,
                  },
                  responseType: "blob",
                })
              ).data;
              const url = URL.createObjectURL(getData);
              window.open(url, "_blank");
              URL.revokeObjectURL(url);
              dispatch(setIsWaitingPriceProposalPreviewResponse(false));
              return;
            }
          });
        }
      } catch (error) {
        dispatch(setIsWaitingPriceProposalPreviewResponse(false));
        console.log(error);
      }
    };

export const getVerificationPreview =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    id: string | undefined,
  ) =>
    async (dispatch: AppDispatch) => {
      if (!id) return;

      const baseUrl = getApiUrl(window.location.hostname, 2);
      dispatch(setIsWaitingPriceProposalPreviewResponse(true));
      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            if (response) {
              const getData: Blob = (
                await axios.get(`${baseUrl}/orders/${id}/pdf-verification`, {
                  timeout: 20000,
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${response.accessToken}`,
                  },
                  responseType: "blob",
                })
              ).data;
              const url = URL.createObjectURL(getData);
              window.open(url, "_blank");
              URL.revokeObjectURL(url);
              dispatch(setIsWaitingPriceProposalPreviewResponse(false));
              return;
            }
          });
        }
      } catch (error) {
        dispatch(setIsWaitingPriceProposalPreviewResponse(false));
        console.log(error);
      }
    };

export const getOrderEmailHistory =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    id: string | undefined,
  ) =>
    async (dispatch: AppDispatch) => {
      if (!id) return;

      const baseUrl = getApiUrl(window.location.hostname, 2);
      try {
        if (account) {
          const response = await acquireApiToken(instance, account);
          if (response) {
            const getData: EmailHistory[] = (
              await axios.get(`${baseUrl}/orders/${id}/email-history`, {
                timeout: 20000,
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${response.accessToken}`,
                },
                responseType: "json",
              })
            ).data;

            dispatch(setEmailHistory(getData));
            return;
          }
        }
      } catch (error) {
        console.log(error);
      }
    };

export const sendOrderEmail =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    id: string | undefined,
    onSuccess: (success: boolean | null) => void,
  ) =>
    async (dispatch: AppDispatch) => {
      if (!id) return;
      const baseUrl = getApiUrl(window.location.hostname, 2);
      dispatch(setIsWaitingSendOrderEmailResponse(true));
      try {
        if (account) {
          const response = await acquireApiToken(instance, account);
          if (response) {
            const result = await axios.post(
              `${baseUrl}/orders/${id}/send-price-proposal`,
              {},
              {
                timeout: 20000,
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${response.accessToken}`,
                },
                responseType: "json",
              },
            );
            if (result.status === 204) {
              onSuccess(true);
              dispatch(setIsWaitingSendOrderEmailResponse(false));
              return true;
            } else {
              dispatch(setIsWaitingSendOrderEmailResponse(false));
              onSuccess(false);
              return;
            }
          }
        }
        dispatch(setIsWaitingSendOrderEmailResponse(false));
        onSuccess(false);
        return;
      } catch (error) {
        dispatch(setIsWaitingSendOrderEmailResponse(false));
        onSuccess(false);
        console.log(error);
        return;
      }
    };

    export const sendVerificationEmail =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    id: string | undefined,
    onSuccess: (success: boolean | null) => void,
  ) =>
    async (dispatch: AppDispatch) => {
      if (!id) return;
      const baseUrl = getApiUrl(window.location.hostname, 2);
      dispatch(setIsWaitingSendOrderEmailResponse(true));
      try {
        if (account) {
          const response = await acquireApiToken(instance, account);
          if (response) {
            const result = await axios.post(
              `${baseUrl}/orders/${id}/pdf-verification-email`,
              {},
              {
                timeout: 20000,
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${response.accessToken}`,
                },
                responseType: "json",
              },
            );
            if (result.status === 204) {
              onSuccess(true);
              dispatch(setIsWaitingSendOrderEmailResponse(false));
              return true;
            } else {
              dispatch(setIsWaitingSendOrderEmailResponse(false));
              onSuccess(false);
              return;
            }
          }
        }
        dispatch(setIsWaitingSendOrderEmailResponse(false));
        onSuccess(false);
        return;
      } catch (error) {
        dispatch(setIsWaitingSendOrderEmailResponse(false));
        onSuccess(false);
        console.log(error);
        return;
      }
    };

export const getOrderArticlesStock =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    id: string | undefined,
  ) =>
    async (dispatch: AppDispatch) => {
      if (!id) return;
      const baseUrl = getApiUrl(window.location.hostname, 2);
      dispatch(setIsWaitingStockResponse(true));
      try {
        if (account) {
          const response = await acquireApiToken(instance, account);
          if (response) {
            const result = await axios.get(
              `${baseUrl}/orders/${id}/stock-update`,
              {
                timeout: 20000,
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${response.accessToken}`,
                },
                responseType: "json",
              },
            );
            if (result.status === 200) {
              dispatch(setArticleStockValues(result.data));
              dispatch(setHasUpdatedStock(true));
              dispatch(setIsWaitingStockResponse(false));
              return;
            } else {
              dispatch(setIsWaitingStockResponse(false));
              return;
            }
          }
        }
        dispatch(setIsWaitingStockResponse(false));
        return;
      } catch (error) {
        dispatch(setIsWaitingStockResponse(false));
        console.log(error);
        return;
      }
    };


export const syncOrderStatuses =
  (
    instance: IPublicClientApplication,
    account: AccountInfo | null,
    onSuccess: (success: boolean | null) => void,
  ) =>
    async (dispatch: AppDispatch) => {
      const baseUrl = getApiUrl(window.location.hostname, 2);
      dispatch(setIsWaitingOrderStatusSyncResponse(true));
      try {
        if (account) {
          acquireApiToken(instance, account).then(async (response) => {
            if (response) {
              const result: AxiosResponse<Article[]> = await axios.patch(
                `${baseUrl}/orders/synchronize-order-statuses`,
                {},
                {
                  timeout: 10000,
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${response.accessToken}`,
                  },
                },
              );

              if (result.status === 204 || result.status === 200) {
                onSuccess(true);
                dispatch(setIsWaitingOrderStatusSyncResponse(false));
              } else {
                onSuccess(false);
                dispatch(setIsWaitingOrderStatusSyncResponse(false));
              }
            }
          });
        }
      } catch (error) {
        console.log(error);
        dispatch(setIsWaitingOrderStatusSyncResponse(false));
      }
    };

const handleOrderForRequest = (order: Order): Order => {
  if (!order.articles) return order;

  order.articles = order.articles.map((article) => ({
    ...article,
    quantity: parseInt(handleFloatToInt(article.quantity)),
    purchasePrice: handleFloatToInt(parseFloat(article.purchasePrice)),
    salesPrice: handleFloatToInt(parseFloat(article.salesPrice)),
    workshopSalesPrice: handleFloatToInt(
      parseFloat(article.workshopSalesPrice),
    ),
    storeSalesPrice: handleFloatToInt(parseFloat(article.storeSalesPrice)),
    vat: parseFloat(handleFloatToInt(article.vat)),
    deliveredQuantity: parseInt(handleFloatToInt(article.deliveredQuantity)),
  }));

  return order;
};
